// src/stores/shoppingListStore.ts
// Updated with frontend consolidation logic and proper category grouping

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import type { ShoppingListItem, RecipeIngredient } from '../types';

interface ShoppingListStore {
  // State
  items: ShoppingListItem[];
  isLoading: boolean;
  isAdding: boolean;
  currentPlanId: string | null;
  consolidateMode: boolean; // Toggle between raw and consolidated view

  // Actions
  loadItems: (userId: string, planId: string, dayNumber?: number[]) => Promise<void>;
  addItem: (userId: string, item: Partial<ShoppingListItem>) => Promise<void>;
  removeItem: (id: string) => Promise<void>;
  toggleChecked: (id: string) => Promise<void>;
  clearCompleted: (userId: string, planId?: string | null, dayNumber?: number | null) => Promise<void>;
  clearAll: (userId: string, planId?: string | null) => Promise<void>;
  clearPlanItems: (planId: string) => Promise<void>;
  setConsolidateMode: (enabled: boolean) => void;

  // Real-time subscription
  subscribeToPlanUpdates: (userId: string) => () => void;

  // Helpers (Frontend Consolidation)
  getConsolidatedItems: () => ShoppingListItem[];
  getItemsByCategory: (consolidated?: boolean) => Record<string, ShoppingListItem[]>;
  getItemsByDay: () => Record<number, ShoppingListItem[]>;
  getItemsByMeal: (dayNumber?: number) => Record<string, ShoppingListItem[]>;
  getTotalPrice: (consolidated?: boolean) => number;
  getStats: (consolidated?: boolean) => {
    total: number;
    completed: number;
    open: number;
    estimatedPrice: number;
  };
}

export const useShoppingListStore = create<ShoppingListStore>((set, get) => ({
  // Initial State
  items: [],
  isLoading: false,
  isAdding: false,
  currentPlanId: null,
  consolidateMode: false,

  // Load Items (with optional filters)
  async loadItems(
    userId: string,
    planId?: string,
    dayNumbers?: number[] // NEW: Array of days to filter by
  ) {
    set({ isLoading: true });

    try {
      let query = supabase
        .from('shopping_list')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      // Filter by plan if provided
      if (planId) {
        query = query.eq('plan_id', planId);
      }

      // Filter by day numbers if provided
      if (dayNumbers && dayNumbers.length > 0) {
        query = query.in('day_number', dayNumbers);
      }

      const { data, error } = await query;

      if (error) throw error;

      set({
        items: data || [],
        isLoading: false
      });
    } catch (error: any) {
      console.error('[shoppingListStore] Load items error:', error);
      set({
        items: [],
        isLoading: false
      });
    }
  },

  // Add Item
  addItem: async (userId: string, item: Partial<ShoppingListItem>) => {
    try {
      set({ isAdding: true });

      const { data, error } = await supabase
        .from('shopping_list')
        .insert({
          user_id: userId,
          plan_id: item.plan_id || null,
          day_number: item.day_number || null,
          meal_type: item.meal_type || null,
          name: item.name!,
          quantity: item.quantity || null,
          category: item.category || 'Miscellaneous',
          checked: false,
          price: item.price || null,
          supermarket: item.supermarket || null,
          image_url: item.image_url || null,
          affiliate_link: item.affiliate_link || null
        })
        .select()
        .single();

      if (error) throw error;

      set((state) => ({
        items: [data, ...state.items],
        isAdding: false
      }));

      toast.success('Item added to shopping list');
    } catch (err) {
      console.error('Error adding item:', err);
      toast.error('Failed to add item');
      set({ isAdding: false });
    }
  },

  // Remove Item
  removeItem: async (id: string) => {
    try {
      const { error } = await supabase
        .from('shopping_list')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        items: state.items.filter((item) => item.id !== id)
      }));

      toast.success('Item removed');
    } catch (err) {
      console.error('Error removing item:', err);
      toast.error('Failed to remove item');
    }
  },

  // Toggle Checked
  toggleChecked: async (id: string) => {
    const item = get().items.find((i) => i.id === id);
    if (!item) return;

    try {
      const { error } = await supabase
        .from('shopping_list')
        .update({ checked: !item.checked })
        .eq('id', id);

      if (error) throw error;

      set((state) => ({
        items: state.items.map((i) =>
          i.id === id ? { ...i, checked: !i.checked } : i
        )
      }));
    } catch (err) {
      console.error('Error toggling item:', err);
      toast.error('Failed to update item');
    }
  },

  // Clear Completed (with optional filters)
  clearCompleted: async (userId: string, planId?: string | null, dayNumber?: number | null) => {
    let completedItems = get().items.filter((item) => item.checked);

    if (planId) {
      completedItems = completedItems.filter(item => item.plan_id === planId);
    }

    if (dayNumber) {
      completedItems = completedItems.filter(item => item.day_number === dayNumber);
    }

    const completedIds = completedItems.map(item => item.id);

    if (completedIds.length === 0) {
      toast('No completed items to remove', { icon: 'ℹ️' });
      return;
    }

    try {
      const { error } = await supabase
        .from('shopping_list')
        .delete()
        .in('id', completedIds);

      if (error) throw error;

      set((state) => ({
        items: state.items.filter((item) => !completedIds.includes(item.id))
      }));

      toast.success(`${completedIds.length} item${completedIds.length > 1 ? 's' : ''} removed`);
    } catch (err) {
      console.error('Error clearing completed items:', err);
      toast.error('Failed to remove items');
    }
  },

  // Clear All (with optional plan filter)
  clearAll: async (userId: string, planId?: string | null) => {
    try {
      let query = supabase
        .from('shopping_list')
        .delete()
        .eq('user_id', userId);

      if (planId) {
        query = query.eq('plan_id', planId);
      }

      const { error } = await query;

      if (error) throw error;

      if (planId) {
        set((state) => ({
          items: state.items.filter(item => item.plan_id !== planId)
        }));
      } else {
        set({ items: [] });
      }

      toast.success('Shopping list cleared');
    } catch (err) {
      console.error('Error clearing shopping list:', err);
      toast.error('Failed to clear shopping list');
    }
  },

  // Clear items for specific plan
  clearPlanItems: async (planId: string) => {
    try {
      const { error } = await supabase
        .from('shopping_list')
        .delete()
        .eq('plan_id', planId);

      if (error) throw error;

      set((state) => ({
        items: state.items.filter(item => item.plan_id !== planId)
      }));
    } catch (err) {
      console.error('Error clearing plan items:', err);
    }
  },

  // Toggle consolidation mode
  setConsolidateMode: (enabled: boolean) => {
    set({ consolidateMode: enabled });
  },

  // Real-time Subscription
  subscribeToPlanUpdates: (userId: string) => {
    const channel = supabase
      .channel(`shopping_list:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shopping_list',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          const { eventType, new: newRecord, old: oldRecord } = payload;

          if (eventType === 'INSERT') {
            set((state) => ({
              items: [newRecord as ShoppingListItem, ...state.items]
            }));
          } else if (eventType === 'UPDATE') {
            set((state) => ({
              items: state.items.map((item) =>
                item.id === (newRecord as ShoppingListItem).id ? (newRecord as ShoppingListItem) : item
              )
            }));
          } else if (eventType === 'DELETE') {
            set((state) => ({
              items: state.items.filter((item) => item.id !== (oldRecord as ShoppingListItem).id)
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  // ==========================================
  // FRONTEND CONSOLIDATION LOGIC
  // ==========================================

  // Get consolidated items (merge duplicates)
  getConsolidatedItems: () => {
    const items = get().items;
    const consolidated: Map<string, ShoppingListItem> = new Map();

    items.forEach(item => {
      const key = `${item.name.toLowerCase()}_${item.plan_id || 'none'}`;

      if (consolidated.has(key)) {
        const existing = consolidated.get(key)!;

        // Merge quantities
        const newQuantity = mergeQuantities(existing.quantity, item.quantity);

        // Keep checked status if ANY instance is checked
        const isChecked = existing.checked || item.checked;

        consolidated.set(key, {
          ...existing,
          quantity: newQuantity,
          checked: isChecked,
          // Store original IDs for potential future use
          consolidated_ids: [...(existing.consolidated_ids || [existing.id]), item.id]
        });
      } else {
        consolidated.set(key, {
          ...item,
          consolidated_ids: [item.id]
        });
      }
    });

    return Array.from(consolidated.values());
  },

  // Get items grouped by category
  getItemsByCategory: (consolidated = true) => {
    const items = consolidated ? get().getConsolidatedItems() : get().items;
    const grouped: Record<string, ShoppingListItem[]> = {};

    items.forEach((item) => {
      const category = item.category || 'Miscellaneous';

      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });

    return grouped;
  },

  // Get items grouped by day
  getItemsByDay: () => {
    const items = get().items;
    const grouped: Record<number, ShoppingListItem[]> = {};

    items.forEach((item) => {
      if (item.day_number) {
        if (!grouped[item.day_number]) {
          grouped[item.day_number] = [];
        }
        grouped[item.day_number].push(item);
      }
    });

    return grouped;
  },

  // Get items grouped by meal type (optionally filtered by day)
  getItemsByMeal: (dayNumber?: number) => {
    let items = get().items;

    if (dayNumber) {
      items = items.filter(item => item.day_number === dayNumber);
    }

    const grouped: Record<string, ShoppingListItem[]> = {};

    items.forEach((item) => {
      const mealType = item.meal_type || 'Miscellaneous';

      if (!grouped[mealType]) {
        grouped[mealType] = [];
      }
      grouped[mealType].push(item);
    });

    return grouped;
  },

  // Get total price
  getTotalPrice: (consolidated = false) => {
    const items = consolidated ? get().getConsolidatedItems() : get().items;
    return items.reduce((sum, item) => sum + (item.price || 0), 0);
  },

  // Get stats
  getStats: (consolidated = false) => {
    const items = consolidated ? get().getConsolidatedItems() : get().items;
    const total = items.length;
    const completed = items.filter((item) => item.checked).length;
    const open = total - completed;
    const estimatedPrice = get().getTotalPrice(consolidated);

    return { total, completed, open, estimatedPrice };
  }
}));

// ==========================================
// HELPER FUNCTIONS
// ==========================================

// Merge quantities intelligently
function mergeQuantities(qty1: string | null, qty2: string | null): string {
  if (!qty1 && !qty2) return '';
  if (!qty1) return qty2 || '';
  if (!qty2) return qty1;

  // Extract numbers and units
  const match1 = qty1.match(/(\d+\.?\d*)\s*(\w+)?/);
  const match2 = qty2.match(/(\d+\.?\d*)\s*(\w+)?/);

  if (!match1 || !match2) return `${qty1}, ${qty2}`;

  const num1 = parseFloat(match1[1]);
  const num2 = parseFloat(match2[1]);
  const unit1 = match1[2] || '';
  const unit2 = match2[2] || '';

  // If same unit, add them
  if (unit1.toLowerCase() === unit2.toLowerCase()) {
    const total = num1 + num2;
    return `${Math.round(total)}${unit1}`;
  }

  // Different units - just concatenate
  return `${qty1}, ${qty2}`;
}